import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authFetch } from '../../utils/authFetch';

// ============================================================================
// Review Slice - RTK
// Manages review mode state and edit tracking for risk assessments
// ============================================================================

// ============================================================================
// Async Thunks - API calls
// ============================================================================

/**
 * Start a review session
 * @param {string} modelId - Model UUID
 * @param {string} assessmentUuid - Assessment UUID
 * @returns {Function} Thunk action
 */
export const startReview = createAsyncThunk(
  'review/startReview',
  async ({ modelId, assessmentUuid }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/manageReview', {
        method: 'POST',
        body: JSON.stringify({
          modelId,
          assessmentUuid,
          operation: 'startReview'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to start review');
      }

      const result = await response.json();

      // NOTE: We don't call fetchAllAssessments() here because:
      // 1. assessmentsSlice.extraReducers already syncs assessment.review_status when this action fulfills
      // 2. Calling it here causes re-renders that disrupt review mode activation
      // 3. If needed, the calling component can refresh data after review mode is established

      return {
        modelId,
        assessmentUuid,
        reviewStatus: result.reviewStatus || 'in_progress',
        startedBy: result.startedBy,
        startedAt: result.startedAt
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Track an edit made during review
 * @param {string} modelId - Model UUID
 * @param {string} assessmentUuid - Assessment UUID
 * @param {string} itemType - Type of item ('risk', 'control', 'residual_risk', 'threat')
 * @param {string} itemId - Item UUID
 * @param {string} field - Field name that was edited
 * @param {*} oldValue - Previous value
 * @param {*} newValue - New value
 * @param {Object} userInfo - User information { name, email, userId }
 * @returns {Function} Thunk action
 */
export const trackEdit = createAsyncThunk(
  'review/trackEdit',
  async ({ modelId, assessmentUuid, itemType, itemId, field, oldValue, newValue, userInfo }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/manageReview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          operation: 'trackEdit',
          modelId,
          assessmentUuid,
          itemType,
          itemId,
          field,
          oldValue,
          newValue,
          userInfo
        })
      });

      if (!response.ok) {
        throw new Error('Failed to track edit');
      }

      console.log('[reviewSlice] Edit tracked:', { itemType, itemId, field });

      return {
        assessmentUuid,
        itemType,
        itemId,
        field,
        newValue,
        userInfo
      };
    } catch (error) {
      console.error('[reviewSlice] Failed to track edit:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Approve a review
 * @param {string} modelId - Model UUID
 * @param {string} assessmentUuid - Assessment UUID
 * @param {string} comments - Optional review comments
 * @returns {Function} Thunk action
 */
export const approveReview = createAsyncThunk(
  'review/approveReview',
  async ({ modelId, assessmentUuid, comments = '' }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/manageAssessment', {
        method: 'POST',
        body: JSON.stringify({
          modelId,
          assessmentUuid,
          operation: 'updateReviewStatus',
          reviewStatus: 'approved'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to approve review');
      }

      const result = await response.json();

      // NOTE: assessmentsSlice.extraReducers syncs assessment.review_status automatically
      // Removed fetchAllAssessments() call to avoid disrupting UI during approval

      return {
        assessmentUuid,
        reviewStatus: 'approved',
        reviewedBy: result.reviewedBy,
        reviewedAt: result.reviewedAt,
        comments
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Reject a review
 * @param {string} modelId - Model UUID
 * @param {string} assessmentUuid - Assessment UUID
 * @param {string} comments - Rejection comments
 * @returns {Function} Thunk action
 */
export const rejectReview = createAsyncThunk(
  'review/rejectReview',
  async ({ modelId, assessmentUuid, comments = '' }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/manageAssessment', {
        method: 'POST',
        body: JSON.stringify({
          modelId,
          assessmentUuid,
          operation: 'updateReviewStatus',
          reviewStatus: 'rejected'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to reject review');
      }

      const result = await response.json();

      // NOTE: assessmentsSlice.extraReducers syncs assessment.review_status automatically
      // Removed fetchAllAssessments() call to avoid disrupting UI during rejection

      return {
        assessmentUuid,
        reviewStatus: 'rejected',
        reviewedBy: result.reviewedBy,
        reviewedAt: result.reviewedAt,
        comments
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ============================================================================
// Initial State
// ============================================================================

const initialState = {
  byAssessment: {},
  loading: false,
  error: null
};

// ============================================================================
// Slice Definition
// ============================================================================

const reviewSlice = createSlice({
  name: 'review',
  initialState,
  reducers: {
    /**
     * Update review progress
     */
    updateReviewProgress: (state, action) => {
      const { assessmentUuid, progress } = action.payload;
      if (!state.byAssessment[assessmentUuid]) {
        state.byAssessment[assessmentUuid] = {};
      }
      state.byAssessment[assessmentUuid].progress = progress;
    },

    /**
     * Go to a specific step
     */
    goToStep: (state, action) => {
      const { assessmentUuid, step } = action.payload;
      if (state.byAssessment[assessmentUuid]) {
        state.byAssessment[assessmentUuid].currentStep = step;
      }
    },

    /**
     * Clear review state for an assessment
     */
    clearReview: (state, action) => {
      const { assessmentUuid } = action.payload;
      delete state.byAssessment[assessmentUuid];
    }
  },

  extraReducers: (builder) => {
    // ============================================================================
    // startReview handlers
    // ============================================================================
    builder
      .addCase(startReview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(startReview.fulfilled, (state, action) => {
        state.loading = false;
        const { modelId, assessmentUuid, reviewStatus, startedBy } = action.payload;

        state.byAssessment[assessmentUuid] = {
          modelId,
          reviewStatus,
          startedBy,
          currentStep: 'risks',
          progress: {},
          edits: {
            risks: {},
            controls: {},
            residualRisks: {},
            threats: {}
          }
        };

        console.log('[reviewSlice] Review started:', { assessmentUuid, modelId });
      })
      .addCase(startReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        console.error('[reviewSlice] Failed to start review:', action.payload);
      });

    // ============================================================================
    // trackEdit handlers
    // ============================================================================
    builder
      .addCase(trackEdit.fulfilled, (state, action) => {
        const { assessmentUuid, itemType, itemId, field, newValue, userInfo } = action.payload;

        // Ensure review state exists
        if (!state.byAssessment[assessmentUuid]) {
          console.warn('[reviewSlice] trackEdit called but review not initialized for:', assessmentUuid);
          return;
        }

        // Ensure edits structure exists
        if (!state.byAssessment[assessmentUuid].edits[itemType]) {
          state.byAssessment[assessmentUuid].edits[itemType] = {};
        }

        // Track the edit
        state.byAssessment[assessmentUuid].edits[itemType][itemId] = {
          itemId,
          fieldName: field,
          newValue,
          editorName: userInfo?.name,
          editorEmail: userInfo?.email,
          editedAt: new Date().toISOString()
        };
      })
      .addCase(trackEdit.rejected, (state, action) => {
        console.error('[reviewSlice] Failed to track edit:', action.payload);
        // Don't block user - edit tracking failure shouldn't prevent editing
      });

    // ============================================================================
    // approveReview handlers
    // ============================================================================
    builder
      .addCase(approveReview.pending, (state) => {
        state.loading = true;
      })
      .addCase(approveReview.fulfilled, (state, action) => {
        state.loading = false;
        const { assessmentUuid, reviewStatus, reviewedBy, comments } = action.payload;

        if (state.byAssessment[assessmentUuid]) {
          state.byAssessment[assessmentUuid].reviewStatus = reviewStatus;
          state.byAssessment[assessmentUuid].reviewedBy = reviewedBy;
          state.byAssessment[assessmentUuid].reviewComments = comments;
        }

        console.log('[reviewSlice] Review approved:', { assessmentUuid });
      })
      .addCase(approveReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        console.error('[reviewSlice] Failed to approve review:', action.payload);
      });

    // ============================================================================
    // rejectReview handlers
    // ============================================================================
    builder
      .addCase(rejectReview.pending, (state) => {
        state.loading = true;
      })
      .addCase(rejectReview.fulfilled, (state, action) => {
        state.loading = false;
        const { assessmentUuid, reviewStatus, reviewedBy, comments } = action.payload;

        if (state.byAssessment[assessmentUuid]) {
          state.byAssessment[assessmentUuid].reviewStatus = reviewStatus;
          state.byAssessment[assessmentUuid].reviewedBy = reviewedBy;
          state.byAssessment[assessmentUuid].reviewComments = comments;
        }

        console.log('[reviewSlice] Review rejected:', { assessmentUuid });
      })
      .addCase(rejectReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        console.error('[reviewSlice] Failed to reject review:', action.payload);
      });
  }
});

// ============================================================================
// Exports
// ============================================================================

export const {
  updateReviewProgress,
  goToStep,
  clearReview
} = reviewSlice.actions;

export default reviewSlice.reducer;
